
import React, { useState, useEffect } from 'react';
import { Mail, Linkedin, Twitter, Send, CheckCircle2, Globe, AlertCircle, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface FormState {
  name: string;
  email: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

const Footer: React.FC = () => {
  const { currentUser } = useApp();
  const [formData, setFormData] = useState<FormState>({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<keyof FormState, boolean>>({
    name: false,
    email: false,
    message: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success'>('idle');

  const validate = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Entity name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return undefined;
      case 'email':
        if (!value.trim()) return 'Work email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Please enter a valid work email';
        return undefined;
      case 'message':
        if (!value.trim()) return 'Please describe your compliance requirements';
        if (value.trim().length < 10) return 'Please provide more detail (min 10 chars)';
        return undefined;
      default:
        return undefined;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Real-time validation if field has been touched
    if (touched[name as keyof FormState]) {
      const error = validate(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validate(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Mark all as touched
    setTouched({ name: true, email: true, message: true });
    
    // Run full validation
    const newErrors: FormErrors = {
      name: validate('name', formData.name),
      email: validate('email', formData.email),
      message: validate('message', formData.message),
    };

    setErrors(newErrors);

    // Check if any errors exist
    if (Object.values(newErrors).some(error => error !== undefined)) {
      return;
    }

    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setTouched({ name: false, email: false, message: false });
      setErrors({});
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }, 1500);
  };

  return (
    <footer id="contact" className="bg-slate-50 pt-24 pb-12 border-t border-slate-200 relative">
      <div className="max-w-7xl mx-auto px-6">
        {!currentUser && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-14 h-14 rounded-xl bg-indigo-600 flex items-center justify-center font-display font-black text-white text-base">MP</div>
                <div>
                  <h3 className="font-display font-bold text-2xl text-slate-900">CA Manthan Paliwal</h3>
                  <p className="text-indigo-600 text-[10px] font-black uppercase tracking-[0.2em] mt-1">Chartered Accountant</p>
                </div>
              </div>
              <p className="text-slate-500 max-w-md mb-12 leading-relaxed text-lg">
                Precision accounting and global compliance ecosystems. We deliver institutional-grade financial discipline.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-600/5 flex items-center justify-center shrink-0 border border-indigo-100">
                    <Mail size={20} className="text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="text-slate-900 font-bold text-xs mb-1 uppercase tracking-widest">Connect</h4>
                    <a href="mailto:office@manthanpaliwal.com" className="text-slate-500 hover:text-indigo-600 transition-colors text-sm">office@manthanpaliwal.com</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-600/5 flex items-center justify-center shrink-0 border border-indigo-100">
                    <Globe size={20} className="text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="text-slate-900 font-bold text-xs mb-1 uppercase tracking-widest">Network</h4>
                    <p className="text-slate-500 text-sm">Global Remote Support</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <a href="#" className="w-14 h-14 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-indigo-600 transition-all shadow-sm">
                  <Linkedin size={22} />
                </a>
                <a href="#" className="w-14 h-14 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-indigo-600 transition-all shadow-sm">
                  <Twitter size={22} />
                </a>
              </div>
            </div>

            <div className="p-10 rounded-[3rem] bg-white border border-slate-200 shadow-xl relative overflow-hidden">
              <AnimatePresence>
                {submitStatus === 'success' && (
                  <motion.div 
                    initial={{ opacity: 0 }} 
                    animate={{ opacity: 1 }} 
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 z-20 bg-white/95 flex flex-col items-center justify-center text-center p-8"
                  >
                    <motion.div
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 200, damping: 20 }}
                    >
                      <CheckCircle2 size={60} className="text-green-500 mb-6" />
                    </motion.div>
                    <h3 className="text-3xl font-display font-bold text-slate-900 mb-2">Request Received</h3>
                    <p className="text-slate-500 text-lg">Your briefing has been queued. We will reach back within 24 hours.</p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setSubmitStatus('idle')}
                      className="mt-8 px-8 py-3 bg-indigo-600 text-white font-bold rounded-xl text-sm"
                    >
                      Send Another Message
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>

              <h3 className="text-3xl font-display font-bold text-slate-900 mb-2">Inquire Services</h3>
              <p className="text-slate-500 mb-10">Start your journey toward perfect financial hygiene.</p>
              
              <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                <div className="space-y-2">
                  <div className="relative">
                    <input 
                      type="text" 
                      name="name"
                      placeholder="Entity Name" 
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={cn(
                        "w-full px-5 py-4 bg-slate-50 border rounded-2xl outline-none transition-all",
                        touched.name && errors.name 
                          ? "border-red-300 bg-red-50/30 focus:border-red-500" 
                          : "border-slate-200 focus:border-indigo-500 focus:bg-white"
                      )} 
                    />
                    {touched.name && errors.name && (
                      <AlertCircle className="absolute right-4 top-1/2 -translate-y-1/2 text-red-500" size={18} />
                    )}
                  </div>
                  <AnimatePresence>
                    {touched.name && errors.name && (
                      <motion.p 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-red-500 text-[10px] font-bold uppercase tracking-widest pl-2"
                      >
                        {errors.name}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                <div className="space-y-2">
                  <div className="relative">
                    <input 
                      type="email" 
                      name="email"
                      placeholder="Work Email" 
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={cn(
                        "w-full px-5 py-4 bg-slate-50 border rounded-2xl outline-none transition-all",
                        touched.email && errors.email 
                          ? "border-red-300 bg-red-50/30 focus:border-red-500" 
                          : "border-slate-200 focus:border-indigo-500 focus:bg-white"
                      )} 
                    />
                    {touched.email && errors.email && (
                      <AlertCircle className="absolute right-4 top-1/2 -translate-y-1/2 text-red-500" size={18} />
                    )}
                  </div>
                  <AnimatePresence>
                    {touched.email && errors.email && (
                      <motion.p 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-red-500 text-[10px] font-bold uppercase tracking-widest pl-2"
                      >
                        {errors.email}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                <div className="space-y-2">
                  <div className="relative">
                    <textarea 
                      name="message"
                      rows={4} 
                      placeholder="Compliance Requirements" 
                      value={formData.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={cn(
                        "w-full px-5 py-4 bg-slate-50 border rounded-2xl outline-none transition-all resize-none",
                        touched.message && errors.message 
                          ? "border-red-300 bg-red-50/30 focus:border-red-500" 
                          : "border-slate-200 focus:border-indigo-500 focus:bg-white"
                      )} 
                    />
                    {touched.message && errors.message && (
                      <AlertCircle className="absolute right-4 top-4 text-red-500" size={18} />
                    )}
                  </div>
                  <AnimatePresence>
                    {touched.message && errors.message && (
                      <motion.p 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-red-500 text-[10px] font-bold uppercase tracking-widest pl-2"
                      >
                        {errors.message}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                <button 
                  type="submit" 
                  disabled={isSubmitting} 
                  className={cn(
                    "w-full flex items-center justify-center gap-2 py-5 text-white font-bold rounded-2xl transition-all shadow-lg active:scale-[0.98]",
                    isSubmitting ? "bg-indigo-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
                  )}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Encrypting Submission...
                    </>
                  ) : (
                    <>
                      Submit Institutional Inquiry
                      <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}

        <div className="pt-10 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-6 text-xs text-slate-400 font-bold uppercase tracking-widest">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white">MP</div>
            <p>© {new Date().getFullYear()} CA Manthan Paliwal</p>
          </div>
          <div className="flex gap-10">
            <a href="#" className="hover:text-indigo-600 transition-colors">Compliance Policy</a>
            <a href="#" className="hover:text-indigo-600 transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
